import React from "react";
import { generateImageSrc } from "@/utils/helper";
import { Check, ChevronDown, ChevronRight, Package } from "lucide-react";

interface Variant {
  id: string;
  title: string;
  image: string;
  sku: string;
  amount: number;
}

interface Product {
  id: string;
  title: string;
  totalVariants: number;
  variants: Variant[];
  images: string[];
  sku: string;
  amount: number;
}

interface ProductCardProps {
  product: Product;
  isSelected: boolean;
  selectedVariantsCount: number;
  isExpanded: boolean;
  onToggleProduct: () => void;
  onToggleExpand: () => void;
  onToggleVariant?: (variant: Variant) => void;
  isVariantSelected?: (variantId: string) => boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  isSelected,
  selectedVariantsCount,
  isExpanded,
  onToggleProduct,
  onToggleExpand,
  onToggleVariant,
  isVariantSelected,
}) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const isPartiallySelected = selectedVariantsCount > 0 && selectedVariantsCount < product.totalVariants;
  const isFullySelected = selectedVariantsCount === product.totalVariants && product.totalVariants > 0;

  return (
    <div className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors">
      {/* Product Row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Checkbox */}
        <div
          className="shrink-0 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onToggleProduct();
          }}
        >
          <input
            type="checkbox"
            checked={isFullySelected}
            onChange={() => {}}
            className="w-4 h-4 rounded border-border cursor-pointer"
            style={{
              accentColor: isFullySelected ? 'var(--primary)' : undefined,
            }}
          />
        </div>

        {/* Product Image */}
        <div className="shrink-0 w-10 h-10 rounded-md overflow-hidden bg-muted">
          {Array.isArray(product?.images) && product.images.length > 0 ? (
            <img
              src={generateImageSrc(product.images[0])}
              alt={product.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-foreground truncate">
            {product.title}
          </p>
          {hasVariants && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {selectedVariantsCount > 0
                ? `${selectedVariantsCount} of ${product.totalVariants} variants selected`
                : `${product.totalVariants} variants`}
            </p>
          )}
        </div>

        {/* SKU and Price */}
        <div className="hidden sm:flex items-center gap-6 text-sm text-muted-foreground">
          {product.sku && (
            <span className="text-xs">{product.sku}</span>
          )}
          {product.amount && (
            <span className="text-xs font-medium">₹{product.amount}</span>
          )}
        </div>

        {/* Expand Button */}
        {hasVariants && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="shrink-0 p-1 hover:bg-muted rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
        )}
      </div>

      {/* Variants List */}
      {hasVariants && isExpanded && (
        <div className="bg-muted/20 border-t border-border">
          {product.variants.map((variant) => {
            const variantSelected = isVariantSelected ? isVariantSelected(variant.id) : false;
            const variantImage = variant.image ? generateImageSrc(variant.image) : null;
            const formattedPrice = variant.amount ? `₹${(variant.amount / 100).toFixed(2)}` : null;
            
            return (
              <div
                key={variant.id}
                onClick={() => onToggleVariant?.(variant)}
                className="flex items-center gap-3 px-4 py-2 pl-14 hover:bg-muted/50 cursor-pointer transition-colors border-b border-border/50 last:border-b-0"
              >
                {/* Variant Checkbox */}
                <input
                  type="checkbox"
                  checked={variantSelected}
                  onChange={() => {}}
                  className="w-4 h-4 rounded border-border cursor-pointer"
                  style={{
                    accentColor: variantSelected ? 'var(--primary)' : undefined,
                  }}
                />

                {/* Variant Image */}
                {variantImage && (
                  <div className="shrink-0 w-8 h-8 rounded overflow-hidden bg-muted border border-border/50">
                    <img
                      src={variantImage}
                      alt={variant.title || "Variant"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Variant Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">
                    {variant.title && variant.title.trim() !== "" ? variant.title : "Default Variant"}
                  </p>
                </div>

                {/* Variant SKU and Price */}
                <div className="hidden sm:flex items-center gap-6 text-sm text-muted-foreground">
                  {variant.sku && (
                    <span className="text-xs font-mono">{variant.sku}</span>
                  )}
                  {formattedPrice && (
                    <span className="text-xs font-semibold text-primary">{formattedPrice}</span>
                  )}
                </div>

                {/* Spacing for alignment with parent expand button */}
                <div className="w-6"></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductCard;
